import React, { useEffect, useMemo, useState } from 'react';
import priceMovementsService from '../../utils/services/priceMovementsService';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES } from '../HotProps/bookmakerAssets';
import PriceHistoryChart from '../HotProps/PriceHistoryChart';
import PriceSparkline from '../HotProps/PriceSparkline';

// ─── module-level cache ─────────────────────────────────────────────────────
//
// Multiple SnipeFeed rows for the same (player, prop_type) — e.g. Daicos's
// 15+, 20+, 25+ Disposals — would otherwise each hit the API on expand. The
// price-movements endpoint already returns every threshold for that combo in
// one response, so we cache by `${sport}|${player}|${prop_type}` and dedupe
// in-flight requests via the shared promise. TTL matches the backend's 60s
// refresh cadence so the cache never serves data older than the source.

const CACHE_TTL_MS = 60 * 1000;
const cache = new Map();

function getCacheKey(sport, player, propType) {
    return `${(sport || 'afl').toLowerCase()}|${(player || '').toLowerCase()}|${(propType || '').toLowerCase()}`;
}

async function fetchWithCache({ sport, player_name, prop_type }) {
    const key = getCacheKey(sport, player_name, prop_type);
    const now = Date.now();
    const existing = cache.get(key);
    if (existing && existing.data && now - existing.ts < CACHE_TTL_MS) {
        return existing.data;
    }
    if (existing && existing.promise) {
        return existing.promise;
    }
    const promise = priceMovementsService
        .getPriceMovements({ sport, player_name, prop_type })
        .then((result) => {
            cache.set(key, { data: result, ts: Date.now() });
            return result;
        })
        .catch((err) => {
            cache.delete(key);
            return { success: false, message: err?.message || 'Network error' };
        });
    cache.set(key, { promise, ts: now });
    return promise;
}

// AFL market → API prop_type. The keywords are mutually exclusive across the
// supported AFL markets (disposals / goals / marks / tackles / fantasy_points /
// kicks / handballs / clearances / hit_outs).
function deriveAflPropType(market) {
    const m = (market || '').toLowerCase();
    if (m.includes('disposal')) return 'disposals';
    if (m.includes('goal')) return 'goals';
    if (m.includes('handball')) return 'handballs';
    if (m.includes('kick')) return 'kicks';
    if (m.includes('mark')) return 'marks';
    if (m.includes('tackle')) return 'tackles';
    if (m.includes('fantasy')) return 'fantasy_points';
    if (m.includes('clearance')) return 'clearances';
    if (m.includes('hit')) return 'hit_outs';
    return null;
}

// ─── small formatters (mirror the HotProps row variants) ───────────────────

function formatOdds(v) {
    if (v == null) return '—';
    return Number(v).toFixed(2);
}

function formatPP(v, { showSign = true } = {}) {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    const sign = n > 0 ? '+' : '';
    return `${showSign ? sign : ''}${n.toFixed(2)}%`;
}

function formatTickTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

// ─── per-bookmaker movement row + nested tick history ───────────────────────
//
// Visually mirrors the HotProps expanded panel exactly so users get a familiar
// inspector regardless of which surface they opened it from.

function TickHistoryTable({ priceChanges }) {
    if (!Array.isArray(priceChanges) || priceChanges.length === 0) {
        return (
            <div className="px-3 py-3 text-center text-[10px] font-mono text-slate-600">
                No ticks recorded.
            </div>
        );
    }
    const reversed = [...priceChanges].reverse();
    const lastIndex = reversed.length - 1;

    return (
        <div className="max-h-[260px] overflow-auto styled-scrollbar">
            <table className="w-full text-left">
                <thead className="sticky top-0 bg-slate-950/95 backdrop-blur-sm">
                    <tr className="border-b border-slate-800/60">
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">#</th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">Time</th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest text-right">Price</th>
                    </tr>
                </thead>
                <tbody>
                    {reversed.map((tick, i) => {
                        const isOpen = i === lastIndex;
                        const isCurrent = i === 0;
                        return (
                            <tr
                                key={`${tick.price_id ?? i}-${tick.timestamp}`}
                                className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/20 transition-colors"
                            >
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-700 tabular-nums">
                                    {tick.price_id != null ? `#${tick.price_id}` : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-400 tabular-nums whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5">
                                        {formatTickTime(tick.timestamp)}
                                        {isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-emerald-400 px-1 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5">
                                                current
                                            </span>
                                        )}
                                        {isOpen && !isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-amber-400 px-1 py-0.5 rounded border border-amber-500/30 bg-amber-500/5">
                                                open
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td className={`px-3 py-1.5 text-[11px] font-mono font-semibold tabular-nums text-right ${
                                    isCurrent ? 'text-emerald-400' : 'text-slate-200'
                                }`}>
                                    {formatOdds(tick.price)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function BookmakerMovementItem({ bk, isBest }) {
    const [ticksOpen, setTicksOpen] = useState(false);
    const positive = (bk.percentage_change || 0) > 0;
    const sparkColor = positive ? '#34d399' : '#f87171';
    const tickCount = Array.isArray(bk.price_changes) ? bk.price_changes.length : 0;
    const canExpand = tickCount > 0;

    return (
        <div>
            <button
                type="button"
                onClick={() => canExpand && setTicksOpen((v) => !v)}
                disabled={!canExpand}
                aria-expanded={ticksOpen}
                className={`w-full grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2 text-left transition-colors ${
                    canExpand ? 'hover:bg-slate-800/40 cursor-pointer' : 'cursor-default'
                } ${ticksOpen ? 'bg-slate-800/30' : ''}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <BookmakerLogo bookmaker={bk.bookmaker} size={18} />
                    <span className="text-xs font-mono text-slate-300 truncate">
                        {BOOKMAKER_DISPLAY_NAMES[bk.bookmaker] || bk.bookmaker}
                    </span>
                    {isBest && (
                        <span className="text-[9px] font-mono text-emerald-400/70 uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/20 whitespace-nowrap">
                            best
                        </span>
                    )}
                </div>

                <div className="hidden sm:flex items-center gap-1 font-mono tabular-nums text-[11px]">
                    <span className="text-slate-500">{formatOdds(bk.open_price)}</span>
                    <svg className="w-3 h-3 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-semibold`}>
                        {formatOdds(bk.current_price)}
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="hidden md:block">
                        <PriceSparkline
                            priceChanges={bk.price_changes}
                            color={sparkColor}
                            width={80}
                            height={20}
                        />
                    </div>
                    <span
                        className={`text-[11px] font-mono font-semibold tabular-nums px-1.5 py-0.5 rounded whitespace-nowrap ${
                            positive ? 'text-emerald-400 bg-emerald-500/5' : 'text-red-400 bg-red-500/5'
                        }`}
                    >
                        {formatPP(bk.percentage_change)}
                    </span>
                    <span className="hidden lg:inline text-[9px] font-mono text-slate-700 whitespace-nowrap tabular-nums">
                        {tickCount}t
                    </span>
                </div>

                <svg
                    className={`w-3 h-3 transition-all ${
                        canExpand
                            ? ticksOpen ? 'rotate-180 text-emerald-400' : 'text-slate-600'
                            : 'text-slate-800'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {ticksOpen && canExpand && (
                <div className="border-t border-slate-800/60 bg-slate-950/80 animate-fade-in">
                    <TickHistoryTable priceChanges={bk.price_changes} />
                </div>
            )}
        </div>
    );
}

function ExpandedDetail({ sortedHistory, best }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-5">
            {/* Per-book breakdown */}
            <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <span>Per-book movement</span>
                    <span className="h-px flex-1 bg-slate-800/60" />
                    <span className="text-slate-600">
                        {sortedHistory.length} {sortedHistory.length === 1 ? 'book' : 'books'}
                    </span>
                </div>
                <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 divide-y divide-slate-800/50 overflow-hidden">
                    {sortedHistory.map((bk) => (
                        <BookmakerMovementItem
                            key={bk.bookmaker}
                            bk={bk}
                            isBest={best && best.bookmaker === bk.bookmaker}
                        />
                    ))}
                </div>
                <p className="mt-1.5 text-[9px] font-mono text-slate-700 tracking-wide">
                    Tap a bookmaker to inspect every recorded price.
                </p>
            </div>

            {/* Overlaid price history chart */}
            <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <span>Price history</span>
                    <span className="h-px flex-1 bg-slate-800/60" />
                </div>
                <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 p-3">
                    <PriceHistoryChart
                        priceHistory={sortedHistory}
                        bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                    />
                </div>
            </div>
        </div>
    );
}

// ─── inline status panels ───────────────────────────────────────────────────

function StatusBox({ children, accent = 'slate' }) {
    const tones = {
        slate: 'border-slate-800/60 bg-slate-950/40 text-slate-500',
        red: 'border-red-500/20 bg-red-500/5 text-red-300',
        amber: 'border-amber-500/20 bg-amber-500/5 text-amber-300',
        emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-300',
    };
    return (
        <div className={`rounded-lg border ${tones[accent] || tones.slate} px-4 py-6 flex flex-col items-center text-center`}>
            {children}
        </div>
    );
}

// ─── main expandable panel ──────────────────────────────────────────────────

function PriceMovementsExpanded({ player, market, propType: explicitPropType, sport = 'afl' }) {
    const propType = useMemo(
        () => explicitPropType || deriveAflPropType(market),
        [explicitPropType, market]
    );

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!player || !propType) {
            setData(null);
            setError(null);
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        fetchWithCache({ sport, player_name: player, prop_type: propType }).then((result) => {
            if (cancelled) return;
            if (result.success) {
                setData(result.data);
            } else {
                setData(null);
                setError(result);
            }
            setIsLoading(false);
        });
        return () => { cancelled = true; };
    }, [player, propType, sport]);

    // The API returns every threshold for the player+prop_type. We narrow to
    // the specific market this row represents (e.g. `25+ Disposals`).
    const matchingProp = useMemo(() => {
        if (!data) return null;
        const props = Array.isArray(data.props) ? data.props : [];
        return props.find((p) => p.market === market) || null;
    }, [data, market]);

    if (!propType) {
        return (
            <StatusBox>
                <p className="text-[11px] font-mono text-slate-600">
                    Price fluctuations aren't tracked for this market type.
                </p>
            </StatusBox>
        );
    }

    if (isLoading) {
        return (
            <div className="py-6 flex flex-col items-center gap-2">
                <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                </div>
                <span className="text-[10px] font-mono text-slate-600">Loading price history</span>
            </div>
        );
    }

    if (error) {
        if (error.isSubscriptionError) {
            return (
                <StatusBox accent="emerald">
                    <p className="text-xs font-semibold text-white mb-1">Subscription Required</p>
                    <p className="text-[11px] font-mono text-slate-500 leading-relaxed mb-3 max-w-[360px]">
                        A Snipe Pro or Snipe Elite subscription is required to view price fluctuations.
                    </p>
                    <a
                        href="/manage-subscriptions"
                        className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded text-[11px] font-mono font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all"
                    >
                        Manage Subscriptions
                    </a>
                </StatusBox>
            );
        }
        const accent = error.errorCode === 'RATE_LIMIT_EXCEEDED'
            ? 'amber'
            : (error.isAuthError ? 'red' : 'slate');
        return (
            <StatusBox accent={accent}>
                <p className="text-xs font-semibold text-white mb-1">
                    {error.isAuthError ? 'Authentication failed' : 'Couldn\'t load fluctuations'}
                </p>
                <p className="text-[11px] font-mono text-slate-500 leading-relaxed max-w-[360px]">
                    {error.message || 'Please try again in a moment.'}
                </p>
            </StatusBox>
        );
    }

    if (!matchingProp) {
        return (
            <StatusBox>
                <p className="text-xs font-semibold text-slate-300 mb-1">No movements yet</p>
                <p className="text-[11px] font-mono text-slate-600 leading-relaxed max-w-[360px]">
                    The bot is tracking this line but no bookmaker has changed its price since tracking started.
                </p>
            </StatusBox>
        );
    }

    const sortedHistory = [...(matchingProp.price_history || [])].sort(
        (a, b) => (b.percentage_change || 0) - (a.percentage_change || 0)
    );
    const best = matchingProp.best_odds || null;

    return <ExpandedDetail sortedHistory={sortedHistory} best={best} />;
}

export default PriceMovementsExpanded;
